import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Home, Check, Star, Sparkles, Camera, Image as ImageIcon, MapPin, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import TestimonialsSection from "../components/TestimonialsSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Upgrade = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const plans = [
    {
      id: "free",
      name: "Free Forever",
      price: 0,
      description: "Perfect for testing the waters",
      features: [
        "Unlimited photos & videos",
        "AI-generated description",
        "AI price suggestion",
        "Basic listing visibility",
        "Buyer inquiry form",
        "Marketing media kit",
        "Analytics dashboard"
      ],
      limitations: [
        "Standard search placement",
        "No homepage feature"
      ],
      badge: null,
      color: "gray"
    },
    {
      id: "featured",
      name: "Featured Listing",
      price: 49,
      period: "per month",
      description: "Get 3x more visibility",
      popular: true,
      features: [
        "Everything in Free, PLUS:",
        "🔥 Top of search results",
        "⭐ Featured badge on listing",
        "📸 AI photo enhancement (5 photos)",
        "🎨 Custom thumbnail borders",
        "📊 Priority in buyer emails",
        "💬 Highlighted in notifications",
        "Cancel anytime"
      ],
      stats: "Featured listings get 3x more views & 2x more inquiries",
      badge: "FEATURED",
      color: "orange"
    },
    {
      id: "premium",
      name: "Premium Pro",
      price: 99,
      period: "per month", 
      description: "Maximum exposure & tools",
      features: [
        "Everything in Featured, PLUS:",
        "🏠 Homepage hero placement",
        "📸 AI photo enhancement (ALL photos)",
        "🖼️ Virtual staging (3 rooms)",
        "📱 Boosted social media posts",
        "🎯 Targeted buyer matching",
        "📧 Email blast to 5,000+ buyers",
        "🥇 'Premium' badge everywhere",
        "Priority customer support"
      ],
      stats: "Premium listings sell 40% faster on average",
      badge: "PREMIUM PRO",
      color: "purple"
    }
  ];

  const oneTimeServices = [
    {
      id: "photo-enhancement",
      name: "Photo Enhancement",
      price: 29,
      icon: Camera,
      description: "AI-powered enhancement for 10 photos",
      benefits: ["Remove clutter", "Improve lighting", "Color correction", "HDR effect"]
    },
    {
      id: "virtual-staging",
      name: "Virtual Staging",
      price: 49,
      icon: ImageIcon,
      description: "Transform empty rooms into furnished spaces",
      benefits: ["3 rooms staged", "Multiple style options", "48-hour delivery", "Unlimited revisions"]
    },
    {
      id: "yard-signs",
      name: "Professional Yard Signs",
      price: 39,
      icon: MapPin,
      description: "2x 18x24\" weather-resistant signs + stakes",
      benefits: ["Free shipping", "Custom QR code", "Weather resistant", "Easy setup"]
    },
    {
      id: "mls-syndication",
      name: "MLS Syndication",
      price: 299,
      icon: TrendingUp,
      description: "One-time fee to list on NTREIS MLS",
      benefits: ["Appears on Zillow/agent.com", "MLS agent visibility", "6-month listing", "Full MLS exposure"]
    }
  ];

  const handleUpgrade = async (planId) => {
    if (!user) {
      toast.error("Please sign in to upgrade");
      navigate('/');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        { listing_id: id, tier: planId },
        { withCredentials: true }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Failed to start checkout");
    } finally {
      setLoading(false);
    }
  };

  const handleOneTimeService = async (serviceId) => {
    toast.info("One-time services coming soon! For now, contact support.");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Scarcity Message */}
        <div className="text-center mb-12 fade-in">
          <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Upgrade Your Listing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get maximum exposure in a market with minimal competition. Your home deserves to be seen by every buyer.
          </p>
        </div>

        {/* Monthly Plans */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Playfair Display' }}>
            Monthly Plans
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <Card 
                key={plan.id}
                className={`relative p-8 ${plan.popular ? 'border-4 border-orange-500 shadow-2xl scale-105' : 'border-2 border-gray-200'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-600 text-white px-6 py-1 rounded-full text-sm font-bold">
                    MOST POPULAR
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    {plan.name}
                  </h3>
                  <div className="mb-2">
                    <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                    {plan.period && <span className="text-gray-600 ml-2">/{plan.period}</span>}
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>

                {plan.stats && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-center">
                    <p className="text-sm text-blue-800 font-semibold">{plan.stats}</p>
                  </div>
                )}

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.id === 'free' ? (
                  <Button 
                    className="w-full bg-gray-600 hover:bg-gray-700"
                    disabled
                  >
                    Current Plan
                  </Button>
                ) : (
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-orange-600 hover:bg-orange-700' : 'bg-purple-600 hover:bg-purple-700'}`}
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={loading}
                    data-testid={`upgrade-${plan.id}-btn`}
                  >
                    {loading ? 'Processing...' : `Upgrade to ${plan.name}`}
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* One-Time Services */}
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Playfair Display' }}>
            One-Time Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {oneTimeServices.map((service) => {
              const Icon = service.icon;
              return (
                <Card key={service.id} className="p-6 hover-lift">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 mx-auto bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{service.name}</h3>
                    <div className="text-3xl font-bold text-blue-600 mb-2">${service.price}</div>
                    <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {service.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleOneTimeService(service.id)}
                    data-testid={`service-${service.id}-btn`}
                  >
                    Add Service
                  </Button>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Playfair Display' }}>
            Featured Listings Sell Faster
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { stat: "3x", label: "More Views", icon: "👀" },
              { stat: "2x", label: "More Inquiries", icon: "💬" },
              { stat: "40%", label: "Faster Sale", icon: "⚡" }
            ].map((item, idx) => (
              <Card key={idx} className="p-6 text-center border-2">
                <div className="text-4xl mb-2">{item.icon}</div>
                <div className="text-4xl font-bold text-orange-600 mb-2" style={{ fontFamily: 'Playfair Display' }}>
                  {item.stat}
                </div>
                <div className="text-gray-600">{item.label}</div>
              </Card>
            ))}
          </div>
        </div>

        {/* Success Stories */}
        <TestimonialsSection maxCards={3} />
      </div>
    </div>
  );
};

export default Upgrade;
